import React, { useEffect, useState } from 'react';
import { Copy, Check, Send, Link2, MailCheck, AlertTriangle, X } from 'lucide-react';
import Modal from './Modal';
import { api, ApiError } from '../../lib/api';

// Replaces "Reset password" (an admin-typed password). Sends the person a
// single-use link instead: an invite if they have never set a password, a
// reset otherwise. Their current password keeps working until they use it.

// D1 timestamps are UTC "YYYY-MM-DD HH:MM:SS".
export function formatLinkExpiry(expiresAt) {
  if (!expiresAt) return '';
  const d = new Date(String(expiresAt).replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function whyNotEmailed(result) {
  if (result.stub) return 'Email sending is switched off in this environment, so nothing was sent.';
  if (result.error) return 'The email could not be sent. Pass the link on yourself instead.';
  return 'Pass this link on yourself, for example by text.';
}

// Result of issuing a link: "emailed" confirmation, or the link to copy.
export function PasswordLinkResult({ email, result }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked: the field is selectable */ }
  };
  const expires = formatLinkExpiry(result.expiresAt);
  if (result.sent) {
    return (
      <div className="flex items-start gap-3" data-testid="link-result-sent">
        <MailCheck className="w-5 h-5 text-[#B0FC38] shrink-0 mt-0.5" aria-hidden="true" />
        <div className="text-[13px] text-gray-300">
          <div className="text-white font-medium">{result.purpose === 'invite' ? 'Invite emailed' : 'Reset link emailed'} to {email}</div>
          <div className="text-gray-500 mt-0.5">It works once{expires ? ` and expires ${expires}` : ''}.</div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="link-result-copy">
      {result.stub || result.error ? (
        <div className="flex items-start gap-2 text-[12px] text-amber-200/90 mb-3">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-px" aria-hidden="true" />
          <span>{whyNotEmailed(result)}</span>
        </div>
      ) : (
        <p className="text-[12px] text-gray-400 mb-3">{whyNotEmailed(result)}</p>
      )}
      <label htmlFor="password-link-url" className="block text-[11px] font-medium text-gray-400/80 mb-1.5 tracking-wide">
        {result.purpose === 'invite' ? 'Invite link' : 'Reset link'} for {email}
      </label>
      <div className="flex gap-2">
        <input
          id="password-link-url"
          readOnly
          value={result.url || ''}
          onFocus={(e) => e.target.select()}
          className="w-full min-w-0 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-[12px] font-mono text-gray-200"
        />
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1.5 px-3 rounded-lg border border-white/[0.08] bg-white/[0.02] text-[12px] text-gray-200 hover:text-white hover:border-white/[0.2] transition-all shrink-0"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-[#B0FC38]" aria-hidden="true" /> : <Copy className="w-3.5 h-3.5" aria-hidden="true" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <p className="mt-2 text-[11px] text-gray-500">Works once{expires ? `, expires ${expires}` : ''}. It signs them in, so share it only with {email}.</p>
    </div>
  );
}

export default function SendPasswordLinkDialog({ open, user, onClose, onSent }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) { setBusy(''); setError(''); setResult(null); }
  }, [open, user?.id]);

  const invite = user && !user.passwordSet;
  const send = async (delivery) => {
    if (busy) return;
    setBusy(delivery);
    setError('');
    try {
      const res = await api.post(`/api/admin/users/${user.id}/password-link`, { delivery });
      setResult(res);
      onSent?.(user, res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message || 'Could not create the link.' : 'Network error, please try again.');
    } finally {
      setBusy('');
    }
  };

  const secondaryBtn = 'inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/[0.1] text-sm text-gray-300 hover:text-white hover:border-white/[0.2] transition-all disabled:opacity-60';
  const primaryBtn = 'inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 transition-all disabled:opacity-60 disabled:cursor-not-allowed';

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={`${invite ? 'Resend invite' : 'Send password reset'} · ${user?.name || ''}`}
      maxWidth="max-w-md"
      footer={
        result ? (
          <div className="flex justify-end"><button type="button" onClick={onClose} className={primaryBtn}>Done</button></div>
        ) : (
          <div className="flex items-center justify-end gap-2 flex-wrap">
            <button type="button" onClick={onClose} disabled={!!busy} className="px-3 py-2.5 text-sm text-gray-400 hover:text-white disabled:opacity-60">Cancel</button>
            <button type="button" onClick={() => send('copy')} disabled={!!busy} className={secondaryBtn}>
              <Link2 className="w-4 h-4" aria-hidden="true" />{busy === 'copy' ? 'Creating…' : 'Copy link instead'}
            </button>
            <button type="button" onClick={() => send('email')} disabled={!!busy} className={primaryBtn}>
              <Send className="w-4 h-4" aria-hidden="true" />{busy === 'email' ? 'Sending…' : invite ? 'Email invite' : 'Email reset link'}
            </button>
          </div>
        )
      }
    >
      {result ? (
        <PasswordLinkResult email={user?.email} result={result} />
      ) : (
        <div className="space-y-3 text-[13px] text-gray-400 leading-relaxed">
          <p>
            {invite ? (
              <>We’ll email <span className="text-white">{user?.email}</span> a new link to set their password. It works once and expires in 3 days.</>
            ) : (
              <>We’ll email <span className="text-white">{user?.email}</span> a link to choose a new password. It works once and expires in 24 hours.</>
            )}
          </p>
          <p className="text-[12px] text-gray-500">
            {invite
              ? 'Any earlier invite for this person stops working.'
              : 'Their current password keeps working until they use the link. When they do, they’re signed out everywhere else.'}
          </p>
          {error && <div className="px-3 py-2 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90" role="alert">{error}</div>}
        </div>
      )}
    </Modal>
  );
}

// After creating a user: "invite emailed", or the link to pass on when it wasn't.
export function InviteNotice({ notice, onClose }) {
  if (!notice) return null;
  return (
    <div
      className="fixed bottom-6 right-6 z-[90] w-[min(26rem,calc(100vw-3rem))] p-4 rounded-xl bg-[#0a0a0f] border border-[#B0FC38]/30 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.7)]"
      role="status"
      data-testid="invite-notice"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white mb-3">User created</div>
          <PasswordLinkResult email={notice.email} result={notice.result} />
        </div>
        <button type="button" onClick={onClose} className="p-1 rounded text-gray-500 hover:text-white" aria-label="Dismiss">
          <X className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
